import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowRight, ChevronRight, Menu, X } from "lucide-react";
import { useT } from "@/lib/i18n";
import { ScrollToTop } from "@/components/ScrollToTop";
import { SupportChip } from "@/components/SupportChip";
import { LangSwitch, TkLogo } from "@/components/tk";
import { getToken, probeMe, type Me } from "@/lib/api";
import { brandName, isIntlSite } from "@/lib/market";

const TELEGRAM_URL = "https://t.me/taklifla_bot";
const CONTACT_EMAIL = "hi@taklifla.uz";

// Catalog shortcuts shown in the mobile menu and footer.
const MENU_CATEGORIES = ["wedding", "nikoh", "birthday", "beshik", "sunnat", "yubiley", "sevgi", "corporate"];
const FOOTER_CATEGORIES = ["wedding", "nikoh", "birthday", "beshik", "sunnat"];

// Subscribe to localStorage so the nav re-renders after a login/logout
// flips the token (cross-tab also syncs via the storage event).
function useAuthState(): { token: string | null; me: Me | null } {
  const [token, setTokenState] = useState<string | null>(getToken());
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "taklifla.token") setTokenState(getToken());
    };
    window.addEventListener("storage", onStorage);
    // Same-tab updates: poll the value once a route changes (cheap, covers
    // login → nav case where we don't get a storage event).
    const id = window.setInterval(() => {
      const t = getToken();
      setTokenState((prev) => (prev === t ? prev : t));
    }, 1000);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (!token) { setMe(null); return; }
    let cancelled = false;
    probeMe()
      .then((m) => { if (!cancelled) setMe(m); })
      .catch(() => { if (!cancelled) setMe(null); });
    return () => { cancelled = true; };
  }, [token]);

  return { token, me };
}

// Compact label for the logged-in user: last four digits of the phone if
// we have it, else the email's local-part, else a generic "Hisobim".
function authBadge(me: Me | null, fallback: string): string {
  if (me?.phone) {
    const tail = me.phone.replace(/\D/g, "").slice(-4);
    return tail ? "··" + tail : fallback;
  }
  if (me?.email) return me.email.split("@")[0]!;
  return fallback;
}

// full    — site nav + full footer (marketing, catalog, help, legal)
// compact — site nav + one-row footer (template detail, editor, login)
// bare    — no chrome; the page renders its own header (checkout, payment
//           result, the owner dashboard with its sidebar)
type ChromeMode = "full" | "compact" | "bare";

function chromeMode(path: string): ChromeMode {
  if (path.startsWith("/checkout")) return "bare";
  if (path === "/dashboard" || path.startsWith("/dashboard/")) {
    if (path === "/dashboard/invitations/new" || path.endsWith("/edit")) return "compact";
    return "bare";
  }
  if (/^\/(templates|shablonlar)\/[^/]+/.test(path)) return "compact";
  if (path === "/login" || path === "/kirish" || path.startsWith("/auth/")) return "compact";
  return "full";
}

export function RootLayout() {
  const t = useT();
  const location = useLocation();
  // Keep document.title in sync with the active locale so screen readers,
  // browser tabs, and shared bookmarks pick up the user's chosen language.
  // GuestPage overrides this with the couple's names while mounted; on
  // unmount it restores `prev`, which is whatever this effect last wrote.
  useEffect(() => {
    // On the international host the brand is Utopian Invitations, not taklifla
    // (the localized doc.title carries the .uz brand); keep the tab title on
    // brand for .com regardless of the in-app language.
    document.title = isIntlSite()
      ? "Utopian Invitations — Beautiful Digital Invitations"
      : t("doc.title");
  }, [t]);
  // The chrome palette and flat background apply only while the app shell
  // is mounted; guest invitation pages render outside it untouched.
  useEffect(() => {
    document.documentElement.classList.add("tk-chrome");
    return () => document.documentElement.classList.remove("tk-chrome");
  }, []);
  const mode = chromeMode(location.pathname);
  return (
    <div className="tk-app">
      {/* Reset scroll on SPA route changes (preserves POP for back/forward). */}
      <ScrollToTop />
      {/* Skip link — first focusable element for keyboard users so they can
          jump past the nav into the main content. Hidden visually until
          focused. */}
      <a href="#main-content" className="skip-link">
        {t("a11y.skip_to_content")}
      </a>
      {mode !== "bare" && <Nav />}
      <main id="main-content" tabIndex={-1}>
        <Outlet />
      </main>
      <SupportChip />
      {mode === "full" && <Footer />}
      {mode === "compact" && <CompactFooter />}
    </div>
  );
}

const navClass = ({ isActive }: { isActive: boolean }) =>
  isActive ? "tk-nav__link active" : "tk-nav__link";

function Nav() {
  const t = useT();
  const { token, me } = useAuthState();
  const loggedIn = !!token;
  const [menuOpen, setMenuOpen] = useState(false);
  // Close the menu on route change so a tap on a link dismisses it.
  const location = useLocation();
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.hash, location.search]);
  const accountLabel = loggedIn ? authBadge(me, t("tk.nav.cabinet")) : t("nav.signin");
  return (
    <>
      <header className="tk-nav">
        <div className="tk-nav__inner">
          <div className="tk-nav__side">
            <TkLogo />
          </div>
          <nav className="tk-nav__links" aria-label={t("a11y.nav_primary")}>
            <NavLink to="/templates" className={navClass}>{t("nav.gallery")}</NavLink>
            <Link to="/#how" className="tk-nav__link">{t("tk.nav.how")}</Link>
            <Link to="/#pricing" className="tk-nav__link">{t("tk.nav.pricing")}</Link>
            <Link to="/#faq" className="tk-nav__link">{t("tk.nav.faq")}</Link>
          </nav>
          <div className="tk-nav__side tk-nav__side--end">
            <LangSwitch />
            <Link to={loggedIn ? "/dashboard" : "/login"} className="tk-nav__account">
              {loggedIn && <span className="tk-nav__dot" aria-hidden="true" />}
              {accountLabel}
            </Link>
            <Link to="/templates" className="tk-btn tk-btn--primary tk-btn--sm tk-btn--flat">
              {t("tk.nav.create")}
            </Link>
            <Link to={loggedIn ? "/dashboard" : "/login"} className="tk-nav__mobile-account">
              {loggedIn && <span className="tk-nav__dot" aria-hidden="true" />}
              {accountLabel}
            </Link>
            <button
              type="button"
              className="tk-nav__burger"
              aria-label={t("tk.nav.open_menu")}
              aria-expanded={menuOpen}
              aria-controls="tk-mobile-menu"
              onClick={() => setMenuOpen(true)}
            >
              <Menu size={24} strokeWidth={1.6} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>
      {/* Rendered beside the header, not inside it: the header's
          backdrop-filter would otherwise trap position:fixed. */}
      {menuOpen && (
        <MobileMenu
          loggedIn={loggedIn}
          accountLabel={accountLabel}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </>
  );
}

function MobileMenu({
  loggedIn,
  accountLabel,
  onClose,
}: {
  loggedIn: boolean;
  accountLabel: string;
  onClose: () => void;
}) {
  const t = useT();
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.overflow;
    root.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      root.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  const links: Array<{ to: string; label: string }> = [
    { to: "/templates", label: t("nav.gallery") },
    { to: "/#how", label: t("tk.nav.how") },
    { to: "/#pricing", label: t("tk.nav.pricing") },
    { to: "/#faq", label: t("tk.nav.faq") },
    { to: "/dashboard", label: t("tk.nav.cabinet") },
  ];
  return (
    <div
      id="tk-mobile-menu"
      className="tk-menu"
      role="dialog"
      aria-modal="true"
      aria-label={t("nav.menu", "Menyu")}
    >
      <div className="tk-menu__head">
        <TkLogo onClick={onClose} />
        <button
          type="button"
          className="tk-nav__burger"
          style={{ display: "inline-flex" }}
          aria-label={t("tk.nav.close_menu")}
          onClick={onClose}
          autoFocus
        >
          <X size={24} strokeWidth={1.6} aria-hidden="true" />
        </button>
      </div>
      <nav className="tk-menu__nav" aria-label={t("a11y.nav_primary")}>
        {links.map((l) => (
          <Link key={l.to} to={l.to} className="tk-menu__link" onClick={onClose}>
            {l.label}
            <ChevronRight size={20} strokeWidth={1.6} aria-hidden="true" />
          </Link>
        ))}
      </nav>
      <div className="tk-menu__section">
        <div className="tk-menu__label">{t("tk.menu.types")}</div>
        <div className="tk-menu__chips">
          {MENU_CATEGORIES.map((id) => (
            <Link key={id} to={`/templates?cat=${id}`} className="tk-chip tk-chip--sm" onClick={onClose}>
              {t(`category.${id}`)}
            </Link>
          ))}
        </div>
        <div className="tk-menu__row">
          <span>{t("tk.menu.language")}</span>
          <LangSwitch variant="seg" />
        </div>
      </div>
      <div className="tk-menu__actions">
        <Link to="/templates" className="tk-btn tk-btn--primary tk-btn--block" onClick={onClose}>
          {t("tk.nav.create")}
          <ArrowRight size={17} strokeWidth={1.8} aria-hidden="true" />
        </Link>
        <Link
          to={loggedIn ? "/dashboard" : "/login"}
          className="tk-btn tk-btn--secondary tk-btn--block"
          onClick={onClose}
        >
          {accountLabel}
        </Link>
      </div>
    </div>
  );
}

function Footer() {
  const t = useT();
  return (
    <footer className="tk-footer" role="contentinfo">
      <div className="tk-footer__inner">
        <div className="tk-footer__top">
          <div className="tk-footer__brand">
            <TkLogo tone="dark" />
            <p className="tk-footer__tagline">{t("tk.footer.tagline")}</p>
          </div>
          <div className="tk-footer__cols">
            <div className="tk-footer__col">
              <div className="tk-footer__label">{t("tk.footer.col.invitations")}</div>
              {FOOTER_CATEGORIES.map((id) => (
                <Link key={id} to={`/templates?cat=${id}`}>{t(`category.${id}`)}</Link>
              ))}
            </div>
            <div className="tk-footer__col">
              <div className="tk-footer__label">{t("tk.footer.col.service")}</div>
              <Link to="/#how">{t("tk.nav.how")}</Link>
              <Link to="/#pricing">{t("tk.nav.pricing")}</Link>
              <Link to="/#faq">{t("tk.nav.faq")}</Link>
              <Link to="/dashboard">{t("tk.nav.cabinet")}</Link>
            </div>
            <div className="tk-footer__col">
              <div className="tk-footer__label">{t("tk.footer.col.contact")}</div>
              <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer">Telegram</a>
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              <Link to="/help">{t("nav.help")}</Link>
            </div>
            <div className="tk-footer__col">
              <div className="tk-footer__label">{t("tk.footer.col.docs")}</div>
              <Link to="/terms">{t("tk.footer.offer")}</Link>
              <Link to="/privacy">{t("tk.footer.privacy")}</Link>
            </div>
          </div>
        </div>
        <div className="tk-footer__bottom">
          <span>© {new Date().getFullYear()} {brandName()}</span>
          <LangSwitch variant="footer" />
        </div>
      </div>
    </footer>
  );
}

function CompactFooter() {
  const t = useT();
  return (
    <footer className="tk-footer tk-footer--compact" role="contentinfo">
      <div className="tk-footer__inner">
        <div className="tk-footer__row">
          <TkLogo tone="dark" />
          <nav className="tk-footer__inline" aria-label={t("tk.footer.col.service")}>
            <Link to="/templates">{t("nav.gallery")}</Link>
            <Link to="/#faq">{t("tk.nav.faq")}</Link>
            <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer">Telegram</a>
            <Link to="/terms">{t("tk.footer.offer")}</Link>
            <Link to="/privacy">{t("tk.footer.privacy")}</Link>
          </nav>
        </div>
        <div className="tk-footer__bottom">
          <span>© {new Date().getFullYear()} {brandName()}</span>
          <LangSwitch variant="footer" />
        </div>
      </div>
    </footer>
  );
}
